import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

// Dominio canónico de producción — las etiquetas OG/canonical/hreflang siempre apuntan aquí,
// nunca a localhost ni al origin actual, para que sean correctas también en dev/staging.
const SITE_URL = "https://ytmethod.com";
// TODO: sustituir por una imagen OG dedicada de 1200x630 — de momento reutiliza el logo
// para que al menos exista una imagen válida al compartir el link.
const DEFAULT_IMAGE = `${SITE_URL}/ytmethod_transparent.png`;

/**
 * Cabecera SEO por-ruta (title, description, canonical, OG/Twitter, hreflang, JSON-LD).
 * Sustituye el <title>/<meta description> estáticos de index.html, que antes eran los
 * mismos para todas las páginas de la SPA.
 */
export default function Seo({ title, description, path = "/", image, noindex = false, jsonLd }) {
  const { i18n } = useTranslation();
  const url = `${SITE_URL}${path}`;
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const ldItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang: mismo path, idiomas alternativos (el selector de idioma es client-side,
          así que apuntamos ambos al mismo URL — el contenido cambia según i18n del navegador) */}
      <link rel="alternate" hrefLang="es" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="YTMethod" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image || DEFAULT_IMAGE} />
      <meta property="og:locale" content={lang === "en" ? "en_US" : "es_ES"} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || DEFAULT_IMAGE} />

      {ldItems.map((item, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(item)}</script>
      ))}
    </Helmet>
  );
}

export { SITE_URL, DEFAULT_IMAGE };
